import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

/**
 * InfoCard - A themed card component for displaying label/value pairs
 *
 * Use this component in sidebars or summary sections to display
 * key information in a compact, readable format.
 *
 * @example
 * // Basic usage
 * <InfoCard>
 *   <InfoCard.Row label="Status" value="Active" />
 *   <InfoCard.Row label="Created" value="Jan 1, 2025" />
 *   <InfoCard.Row label="Items" value={42} />
 * </InfoCard>
 *
 * @example
 * // With title
 * <InfoCard title="Summary">
 *   <InfoCard.Row label="Total" value="$100.00" />
 * </InfoCard>
 *
 * @example
 * // With custom value rendering
 * <InfoCard>
 *   <InfoCard.Row label="Status">
 *     <Badge variant="success">Active</Badge>
 *   </InfoCard.Row>
 * </InfoCard>
 */

const CardContainer = styled.div`
  padding: 16px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 8px;
`;

const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)'};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
`;

const RowLabel = styled.span`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
`;

const RowValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  text-align: right;
`;

// Row subcomponent
const Row = ({ label, value, children }) => {
  return (
    <RowContainer>
      <RowLabel>{label}</RowLabel>
      {children ? children : <RowValue>{value ?? '-'}</RowValue>}
    </RowContainer>
  );
};

Row.propTypes = {
  /** Label text displayed on the left */
  label: PropTypes.string.isRequired,
  /** Value displayed on the right (can be string, number, or any renderable) */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.node,
  ]),
  /** Custom content to render instead of value (for badges, links, etc.) */
  children: PropTypes.node,
};

// Main InfoCard component
const InfoCard = ({ title, children, className, style }) => {
  return (
    <CardContainer className={className} style={style}>
      {title && <CardTitle>{title}</CardTitle>}
      {children}
    </CardContainer>
  );
};

InfoCard.propTypes = {
  /** Optional title displayed at the top */
  title: PropTypes.string,
  /** InfoCard.Row components */
  children: PropTypes.node.isRequired,
  /** Additional className */
  className: PropTypes.string,
  /** Inline styles */
  style: PropTypes.object,
};

// Attach Row as a static property
InfoCard.Row = Row;

export default InfoCard;
